import {
  type ForwardedRef,
  type PropsWithChildren,
  type ReactElement,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { SlabTabContext } from './SlabTabContext';

interface SlabTabProviderProps<T extends string> extends PropsWithChildren {
  initialTab: T;
}

export interface SlabTabProviderRefProps<T extends string> {
  changeTab: (name: T) => void;
}

const SlabTabProviderRef = <T extends string>(
  { children, initialTab }: SlabTabProviderProps<T>,
  ref: ForwardedRef<SlabTabProviderRefProps<T>>,
): ReactElement => {
  const [activeTab, setActiveTab] = useState(initialTab);

  const changeTab = (name: T): void => setActiveTab(name);

  useImperativeHandle(ref, () => ({
    changeTab,
  }));

  const value = useMemo(() => ({ activeTab, changeTab }), [activeTab, changeTab]);

  return <SlabTabContext.Provider value={value}>{children}</SlabTabContext.Provider>;
};

export const SlabTabProvider = forwardRef(SlabTabProviderRef);
